import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router/dist/lib/context';
import { FeatureFlag, RealmRole } from '@web/common';
import { AuthenticatedRoot } from '../view/authenticated/AuthenticatedRoot';
import { Routes } from './Routes';
import { ParticipantDetail } from '../component';
import { FAQPage } from '../view/public/faq/FAQPage';
import { getFeature } from '../util/ff/getFeature';
import {
  Root,
  Datenschutz,
  Impressum,
  NotEligible,
  LandingPage,
  Nutzungsbedingungen,
  Register,
  VerifyEmail,
  EntryQuestionnaire,
  EnrollLandingPage,
  Deactivation,
  VerifyDeactivation,
} from '../view/public';
import {
  InitialCannabisConsumptionQuestionnaire,
  RepeatedCannabisConsumptionQuestionnaire,
  Dashboard as ParticipantDashboard,
  BookInPerson,
  Deactivated,
  ShopScreen,
  ProfileScreen,
  StammdatenQuestionnaire,
  OrderDetail,
  PointsScreen,
  SimpleOnboardingChecklist,
  RescheduleAppointment,
  RafflesOverviewScreen,
  ActiveRafflesScreen,
  PastRafflesScreen,
  FutureRafflesScreen,
  RaffleDetailsScreen,
  RafflesLayout,
  InitialSF12Questionnaire,
  RepeatedSF12Questionnaire,
} from '../view/authenticated/participant';
import {
  ViewSF12Questionnaire,
  ViewCannabisConsumptionQuestionnaire,
  ViewStammdatenQuestionnaire,
  ViewEntryQuestionnaire,
} from '../component/participantDataView';
import {
  OpenShopOrderWrapper,
  OrderDetailsWrapper,
  OrganizerDashboard,
  UserDetail,
  UserFilterList,
} from '../view/authenticated/organizer';
import {
  InventoryView,
  PausedUsersView,
  StatisticsView,
} from '../view/authenticated/study-owner';
import { QuickEnrollPage } from '../view/public/quick-enroll/QuickEnrollPage';
import { CohortType } from '@prisma/client';
import { AuthenticatedGlobalStores } from '../view/authenticated/AuthenticatedGlobalStores';
import { RepeatedQuestionnaireWrapper } from '../view/authenticated/participant/repeated-questionnaire/RepeatedQuestionnaireDialog/RepeatedQuestionnaireWrapper';
import { QuestionnairesScreen } from '../view/authenticated/participant/questionnaires/QuestionnairesScreen';
import { VersandScreenWrapper } from '../view/authenticated/participant/versand/VersandScreenWrapper';
import { VoucherView } from '../view/authenticated/study-owner/voucher';
import { RaffleWinnersWrapper } from '../view/authenticated/organizer/raffle-winners';

export const createRoutes = (
  realmRole: null | RealmRole,
  cohortType: null | CohortType,
) => {
  return createBrowserRouter([
    {
      path: Routes.Root,
      Component: Root,
      children: [
        {
          index: true,
          Component: LandingPage,
        },
        { path: Routes.Enroll, Component: EnrollLandingPage },
        { path: Routes.QuickEnroll, Component: QuickEnrollPage },
        { path: Routes.Faq, Component: FAQPage },
        {
          path: Routes.EntryQuestionnaire,
          Component: EntryQuestionnaire,
        },
        {
          path: Routes.Register,
          Component: Register,
        },
        {
          path: Routes.VerifyEmail,
          Component: VerifyEmail,
        },
        {
          path: Routes.VerifyDeactivation,
          Component: VerifyDeactivation,
        },
        {
          path: Routes.SelfDeactivation,
          Component: Deactivation,
        },
        {
          path: Routes.NotEligible,
          Component: NotEligible,
        },
        {
          path: Routes.Nutzungsbedingungen,
          Component: Nutzungsbedingungen,
        },
        {
          path: Routes.Datenschutz,
          Component: Datenschutz,
        },
        {
          path: Routes.Impressum,
          Component: Impressum,
        },
        {
          path: Routes.Study,
          Component: () => (
            <AuthenticatedGlobalStores>
              <AuthenticatedRoot />
              <RepeatedQuestionnaireWrapper />
            </AuthenticatedGlobalStores>
          ),
          children: authenticatedRoutes(realmRole, cohortType),
        },
        catchAllRoute,
      ],
    },
  ]);
};

const authenticatedRoutes = (
  realmRole: null | RealmRole,
  cohortType: null | CohortType,
): RouteObject[] => {
  const employeeAndOrganizerShopRoutes =
    getFeature(FeatureFlag.Shop, [
      {
        path: Routes.OpenShopOrders,
        Component: OpenShopOrderWrapper,
      },
      {
        path: `${Routes.OpenShopOrders}/:orderId`,
        Component: OrderDetailsWrapper,
      },
    ]) ?? [];

  const employeeAndOrganizerRaffleRoutes =
    getFeature(FeatureFlag.Raffles, [
      {
        path: Routes.RaffleWinners,
        Component: RaffleWinnersWrapper,
      },
    ]) ?? [];

  // The saved page is already open, so the next dashboard visit must not open it again.
  if (
    realmRole &&
    sessionStorage.getItem('redirectLocation') === window.location.pathname
  ) {
    sessionStorage.removeItem('redirectLocation');
  }

  switch (realmRole) {
    case RealmRole.Deactivated: {
      return [
        {
          index: true,
          Component: Deactivated,
        },
      ];
    }
    case RealmRole.Participant: {
      const shop =
        getFeature(FeatureFlag.Shop, [
          {
            path: `${Routes.ShopOrder}/:orderId`,
            Component: OrderDetail,
          },
          {
            path: Routes.Shop,
            Component: ShopScreen,
          },
        ]) ?? [];

      const raffles =
        getFeature(FeatureFlag.Raffles, [
          {
            path: Routes.Raffles,
            Component: RafflesLayout,
            children: [
              {
                index: true,
                Component: RafflesOverviewScreen,
              },
              {
                path: Routes.ActiveRaffles,
                Component: ActiveRafflesScreen,
              },
              {
                path: Routes.PastRaffles,
                Component: PastRafflesScreen,
              },
              {
                path: Routes.FutureRaffles,
                Component: FutureRafflesScreen,
              },
              {
                path: ':raffleId',
                Component: RaffleDetailsScreen,
              },
            ],
          },
        ]) ?? [];

      return [
        {
          index: true,
          Component: ParticipantDashboard,
        },
        {
          path: Routes.SimpleOnboardingChecklist,
          Component: SimpleOnboardingChecklist,
        },
        ...raffles,
        ...(cohortType &&
        (
          [CohortType.ACCESS, CohortType.CONVENIENT_ACCESS] as string[]
        ).includes(cohortType)
          ? [
              {
                path: Routes.Profile,
                Component: ProfileScreen,
              },
            ]
          : []),
        {
          path: Routes.Points,
          Component: PointsScreen,
        },
        {
          path: Routes.Questionnaires,
          Component: QuestionnairesScreen,
        },
        // the screen itself also requires nachversandEligible, which is only known after /users/me
        ...(cohortType === CohortType.CONVENIENT_ACCESS
          ? [
              {
                path: Routes.Versand,
                Component: VersandScreenWrapper,
              },
            ]
          : []),
        ...shop,
        {
          path: Routes.StammdatenQuestionnaire,
          Component: StammdatenQuestionnaire,
        },
        {
          path: Routes.CannabisConsumptionQuestionnaire,
          Component: InitialCannabisConsumptionQuestionnaire,
        },
        {
          path: Routes.SF12Questionnaire,
          Component: InitialSF12Questionnaire,
        },
        {
          path: Routes.RepeatedCannabisConsumptionQuestionnaire,
          Component: RepeatedCannabisConsumptionQuestionnaire,
        },
        {
          path: Routes.RepeatedSF12Questionnaire,
          Component: RepeatedSF12Questionnaire,
        },
        {
          path: Routes.BookInPerson,
          Component: BookInPerson,
        },
        {
          path: Routes.RescheduleAppointment,
          Component: RescheduleAppointment,
        },
        {
          path: '*',
          Component: () => <Navigate to={Routes.Study} />,
        },
      ];
    }
    case RealmRole.ShopEmployee: {
      return [
        {
          index: true,
          Component: OrganizerDashboard,
        },
        {
          path: `${Routes.User}/:userId`,
          Component: UserDetail,
          children: [
            {
              index: true,
              element: <ParticipantDetail />,
            },
            {
              path: Routes.UserDetailEntryQuestionnaire,
              element: <ViewEntryQuestionnaire />,
            },
            {
              path: Routes.UserDetailStammdatenQuestionnaire,
              element: <ViewStammdatenQuestionnaire />,
            },
            {
              path: Routes.UserDetailCannabisConsumptionQuestionnaire,
              element: <ViewCannabisConsumptionQuestionnaire />,
            },
            {
              path: Routes.UserDetailSF12Questionnaire,
              element: <ViewSF12Questionnaire />,
            },
          ],
        },
        ...employeeAndOrganizerShopRoutes,
        ...employeeAndOrganizerRaffleRoutes,
        {
          path: '*',
          Component: () => <Navigate to={Routes.Study} />,
        },
      ];
    }
    case RealmRole.StudyOrganizer: {
      return [
        {
          index: true,
          Component: OrganizerDashboard,
        },
        {
          path: `${Routes.User}/:userId`,
          Component: UserDetail,
          children: [
            {
              index: true,
              element: <ParticipantDetail />,
            },
            {
              path: Routes.UserDetailEntryQuestionnaire,
              element: <ViewEntryQuestionnaire />,
            },
            {
              path: Routes.UserDetailStammdatenQuestionnaire,
              element: <ViewStammdatenQuestionnaire />,
            },
            {
              path: Routes.UserDetailCannabisConsumptionQuestionnaire,
              element: <ViewCannabisConsumptionQuestionnaire />,
            },
            {
              path: Routes.UserDetailSF12Questionnaire,
              element: <ViewSF12Questionnaire />,
            },
          ],
        },
        ...employeeAndOrganizerShopRoutes,
        ...employeeAndOrganizerRaffleRoutes,
        {
          path: Routes.PausedUsers,
          Component: PausedUsersView,
        },
        {
          path: Routes.Statistics,
          Component: StatisticsView,
        },
        {
          path: Routes.FilterUsers,
          Component: UserFilterList,
        },
        {
          path: Routes.Inventory,
          Component: InventoryView,
        },
        {
          path: Routes.Voucher,
          Component: VoucherView,
        },
        {
          path: '*',
          Component: () => <Navigate to={Routes.Study} />,
        },
      ];
    }
    case RealmRole.Admin: {
      return [
        {
          path: '',
          element: <div>not implemented yet</div>,
        },
        {
          path: '*',
          Component: () => <Navigate to={Routes.Study} />,
        },
      ];
    }

    default: {
      const currentPath = window.location.pathname;
      if (currentPath.length > 1) {
        // since we dont want to save paths like that one: '/'
        sessionStorage.setItem('redirectLocation', window.location.pathname);
      }

      console.log(
        `Could not determine role. Not appending any protected routes`,
      );
      return [];
    }
  }
};

const catchAllRoute: RouteObject = {
  path: '*',
  Component: () => <Navigate to={Routes.Root} />,
};
